import { useCallback, useState, useEffect } from 'react'
import useLocation from 'react-use/lib/useLocation'

import useSiteMetadata from './useSiteMetadata'
import useBrand from './useBrand'
import { urlPathJoin, UrlPathJoinOptions } from '../utils/url'

interface UseUrlPathBuilder {
  blogPrefix: string
  baseURL: string
  urlPathBuilder: (
    paths: string[],
    options?: Omit<UrlPathJoinOptions, 'trailingSlash'>
  ) => string
}

/**
 * useUrlPathBuilder is a hook that provides a function to build a URL according
 * to the business rules of the surrounding website. This means, that you can
 * focus on building the path and this will handle things like the
 * trailingSlash that might or might not be needed.
 */
export default function useUrlPathBuilder(): UseUrlPathBuilder {
  const { trailingSlash } = useSiteMetadata()
  const { blogPrefix = 'blog', productionUrl } = useBrand()
  const { host } = useLocation()
  const [baseURL, setBaseURL] = useState(productionUrl ?? '')
  const urlPathBuilder = useCallback<UseUrlPathBuilder['urlPathBuilder']>(
    (paths, options = {}) => urlPathJoin(paths, { trailingSlash, ...options }),
    [trailingSlash]
  )

  useEffect(() => setBaseURL((url) => (host ? `https://${host}` : url)), [host])

  return { blogPrefix, baseURL, urlPathBuilder }
}
